import React from 'react';
import {
    IOption,
    IOrderDetails,
    IProductDetail,
    ISelectedOptionIds,
    sides,
} from '../../../../models/product-detail';
import { isOptionCategoryRequired } from '../validation';
import ToolTipAnchor from '../../../common/tooltip';
import {
    CHARGER_OPTIONS_ID_LIST,
    DE,
    FR,
    RECEIVER_GAIN_OPTIONS_ID,
} from '../../../common/constants';
import { useIntl } from 'react-intl';

interface IOptionHeaderProps {
    option: IOption;
    productDetails: IProductDetail;
    orderDetails: IOrderDetails;
    patientOrder?: boolean;
    selectedIdsState: ISelectedOptionIds;
    countryCode: string;
}

export default function OptionHeader({
    option,
    productDetails,
    orderDetails,
    selectedIdsState,
    patientOrder,
    countryCode,
}: IOptionHeaderProps) {
    const intl = useIntl();
    const getOptionCategoryNote = (optionCategory: IOption) => {
        const isChargerCategory = CHARGER_OPTIONS_ID_LIST.includes(
            optionCategory.id
        );
        const stockNoteDE = intl.formatMessage({
            id: 'Chargers_Stock_Info_Message_DE',
            defaultMessage:
                'Please adjust the quantity of standard and premium chargers needed for this order. An upcharge will be applied to all Premium chargers selected.',
        });
        const standardPatientNoteDE = intl.formatMessage({
            id: 'Upcharge_Stock',
            defaultMessage:
                'An upcharge will be applied to all Premium chargers selected.',
        });
        const noMultipleStockChargerNote = intl.formatMessage({
            id: 'Charger_Option_Note_Stock_No_Multiple_Options',
            defaultMessage:
                'The total quantity of chargers for this order has been predetermined by the quantity of hearing aids ordered above.',
        });
        const powerAdapterFranceNote = intl.formatMessage({
            id: 'Charger_Option_Note_Addition_Stock_France',
            defaultMessage:
                'The POWER ADAPTER is not included. It can be added to the order by selecting it under Order Options.',
        });
        const isMultipleOptions = optionCategory.items[0].items.length >= 2;
        if (isChargerCategory) {
            if (!isMultipleOptions) {
                if (countryCode === FR) {
                    if (orderDetails.isCustom) return powerAdapterFranceNote;
                    if (!patientOrder)
                        return (
                            noMultipleStockChargerNote + powerAdapterFranceNote
                        );
                }
                if (patientOrder) return '';
                return noMultipleStockChargerNote;
            }
            if (countryCode === DE) {
                if (patientOrder && !orderDetails.isCustom)
                    return standardPatientNoteDE;
                if (!patientOrder) return stockNoteDE;
            }
            if (patientOrder) {
                return intl.formatMessage({
                    id: 'Charger_Option_Note_Patient',
                    defaultMessage:
                        'An upcharge will be applied when selecting a different charger than the default.',
                });
            }
        }
        return optionCategory.note;
    };

    /**
     * Kinda dumb, but a receiver gain tooltip displaying a pdf guide link is only supposed to show for
     * G23 products. This should only filter out G23 products with id 19 (the receiver gain option id)
     */
    const shouldDisplayNote = (optionCategory: IOption) => {
        if (
            optionCategory.id === RECEIVER_GAIN_OPTIONS_ID &&
            patientOrder &&
            productDetails.isSnapFit2
        ) {
            return true;
        }
        return false;
    };

    return (
        option.items.length > 0 && (
            <h3 className={`no-text-transform`}>
                {option.name}
                {isOptionCategoryRequired(
                    option,
                    selectedIdsState,
                    orderDetails.side === 'B'
                        ? sides.binaural
                        : orderDetails.side === 'R'
                        ? sides.right
                        : sides.left
                ) && (
                    <span
                        className="fas fa-asterisk required-red"
                        style={{
                            fontSize: '8px',
                        }}
                    />
                )}
                {option.note && getOptionCategoryNote(option) && (
                    <ToolTipAnchor
                        toolTipMessage={getOptionCategoryNote(option)}
                    />
                )}
            </h3>
        )
    );
}
